import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import { useKiCode } from './useKiCode';
import { useKiCodeSessionHost } from './KiCodeStateProvider';
import { KiCodePanelFrame } from './panel/KiCodePanelFrame';
import { KiCodeSession } from './session/KiCodeSession';
import { saveKiCodeResume, type KiCodeResume } from './session/resumeStorage';

const KICODE_ROOT_ID = 'kicode-root';

const getKiCodeRoot = () => {
  const existing = document.getElementById(KICODE_ROOT_ID);
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = KICODE_ROOT_ID;
  document.body.appendChild(root);
  return root;
};

const CloseKiCodeFallback = ({ onReset }: { onReset: () => void }) => {
  const { close, session } = useKiCode();
  const [failed] = useState(session);
  useEffect(close, [close]);
  useEffect(() => {
    if (session && session !== failed) onReset();
  }, [session, failed, onReset]);
  return null;
};

/**
 * Renders the KiCode panel for the session held by KiCodeStateProvider.
 * Mounted below PluginContextWrapper because the session reads PluginContext.
 */
export const KiCodeHost = () => {
  const [container] = useState(getKiCodeRoot);
  const { close } = useKiCode();
  const { session, identity, dispatch } = useKiCodeSessionHost();

  const startNewConversation = useCallback(
    () => dispatch({ type: 'new_conversation' }),
    [dispatch]
  );
  const onAppCreated = useCallback(
    (appApiName: string) => dispatch({ type: 'app_created', appApiName }),
    [dispatch]
  );

  const onResumable = useCallback(
    (resume: KiCodeResume) => saveKiCodeResume(identity, resume),
    [identity]
  );

  const panel = useMemo(
    () =>
      session ? (
        <ErrorBoundaryResettable
          key={session.key}
          FallbackComponent={CloseKiCodeFallback}
          sentryErrorBoundary
        >
          <KiCodeSession
            session={session}
            onClose={close}
            onStartNewConversation={startNewConversation}
            onAppCreated={onAppCreated}
            onResumable={onResumable}
          />
        </ErrorBoundaryResettable>
      ) : null,
    [session, close, startNewConversation, onAppCreated, onResumable]
  );

  return createPortal(
    <ErrorBoundaryResettable
      FallbackComponent={CloseKiCodeFallback}
      sentryErrorBoundary
    >
      <KiCodePanelFrame>{panel}</KiCodePanelFrame>
    </ErrorBoundaryResettable>,
    container
  );
};
